/**
 * Making a deploy actually arrive.
 *
 * The service worker precaches the app shell, so a returning passenger is served the
 * build their browser already holds. A newly deployed worker installs, precaches, and
 * then WAITS — by specification it does not take over while an old one still controls a
 * page. For a site you visit in a tab that resolves itself eventually. For an installed
 * PWA it does not: the app is never "closed", so the waiting worker can sit there for
 * weeks and every deploy looks like it never happened.
 *
 * That is the whole reason a shipped feature could be live on the server and absent on
 * the phone. The fix is two halves and both are needed:
 *
 *   sw.ts     skipWaiting() + clientsClaim() — the new worker activates and takes over
 *             instead of queueing behind the old one.
 *   this file the page notices it was taken over and reloads, so the passenger is looking
 *             at the build that just activated rather than the one still in memory.
 *
 * The reload is deferred while a passenger is choosing seats or paying. Those two screens
 * hold state that only exists in the tab — a seat hold being confirmed, a payment being
 * redirected — and reloading under someone mid-payment to show them a newer Help page is
 * a bad trade. Everywhere else a reload costs nothing: the app refetches on mount.
 */

/**
 * Routes where a reload would cost the passenger something.
 *
 * Deliberately a small, explicit list rather than "anything with a form". Being wrong in
 * the safe direction here means a deploy waits until the passenger leaves checkout, which
 * is seconds; being wrong in the other direction means interrupting a payment.
 */
const HOLD_PATHS: readonly RegExp[] = [
  /^\/checkout(\/|$)/,
  /^\/trip\/[^/]+\/seats(\/|$)/,
];

/** Whether the app can be reloaded from under the passenger right now. */
export function isSafeToReload(pathname: string): boolean {
  return !HOLD_PATHS.some((pattern) => pattern.test(pathname));
}

/**
 * How often an installed app asks whether a new build exists.
 *
 * A browser only re-fetches sw.js on navigation, and an installed PWA that is never
 * navigated away from makes none. Without this poll the update check would depend on the
 * passenger closing the app, which is the situation being fixed.
 */
export const UPDATE_POLL_MS = 30 * 60 * 1000;

export interface UpdateWatchOptions {
  /** Called when the new worker has taken control and reloading is appropriate. */
  onActivated: () => void;
  /** Test seam. */
  container?: ServiceWorkerContainer;
  pollMs?: number;
}

/**
 * Watches for a newly activated service worker and asks the browser to look for one.
 *
 * Returns a teardown function. `onActivated` may fire at most once — after a controller
 * change the page is stale by definition, so there is nothing to watch for afterwards.
 */
export function watchForActivation(options: UpdateWatchOptions): () => void {
  const container =
    options.container ?? (typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined);
  if (!container) return () => {};

  let done = false;
  const onControllerChange = () => {
    if (done) return;
    done = true;
    options.onActivated();
  };
  container.addEventListener('controllerchange', onControllerChange);

  // Ask now and on a timer. `update()` bypasses the HTTP cache for sw.js by
  // specification, and nginx serves it no-store anyway, so this is a cheap conditional
  // request that costs nothing when there is no new build.
  let timer: ReturnType<typeof setInterval> | undefined;
  void container.ready
    .then((registration) => {
      void registration.update();
      timer = setInterval(() => {
        void registration.update();
      }, options.pollMs ?? UPDATE_POLL_MS);
    })
    .catch(() => {
      // No registration (a browser with service workers disabled, or a first load that
      // failed to register). Nothing to watch; the app works without it.
    });

  return () => {
    done = true;
    container.removeEventListener('controllerchange', onControllerChange);
    if (timer !== undefined) clearInterval(timer);
  };
}
